import * as THREE from "three";
import type { XRRig } from "./session";
import { XRLocomotion } from "./locomotion";

export interface Interactable {
  object: THREE.Object3D;
  onSelect: () => void;
  /** Optional hover feedback; receives true on hover-enter, false on exit. */
  onHover?: (hovered: boolean) => void;
}

/**
 * Per-hand controller handle exposed to grab/input managers.
 * `targetRay` is the WebXR target-ray space; `grip` is the grip space for attach.
 */
export interface ControllerHandle {
  index: number;
  targetRay: THREE.Group;
  grip: THREE.Group;
  selecting: boolean;
  squeezing: boolean;
  /** Optional ownership token for a shared grab manager (hands specialist). */
  owned: THREE.Object3D | null;
}

export interface InputListener {
  onSelectStart?(controller: ControllerHandle): void;
  onSelectEnd?(controller: ControllerHandle): void;
  onSqueezeStart?(controller: ControllerHandle): void;
  onSqueezeEnd?(controller: ControllerHandle): void;
  /** Fired when a controller connects or disconnects (Quest drop / session). */
  onControllerConnection?(controller: ControllerHandle, connected: boolean): void;
}

/**
 * Shared input pipeline for desktop and XR.
 *
 * Controllers initialize in one path (target-ray + grip + select/squeeze) so a
 * future grab manager can subscribe via {@link addListener} without forking
 * device-specific code. Ray-based {@link Interactable} registration remains for
 * UI / simple select targets.
 *
 * Session boundaries must call {@link reset} to clear sticky select/squeeze,
 * phantom ownership, and locomotion stick/snap state.
 */
export class InteractionSystem {
  private readonly rig: XRRig;
  private readonly interactables: Interactable[] = [];
  /** Parallel cache for raycasts — avoids per-frame `.map` allocations. */
  private readonly interactableObjects: THREE.Object3D[] = [];
  private readonly raycaster = new THREE.Raycaster();
  private readonly listeners = new Set<InputListener>();

  private readonly controllers: ControllerHandle[] = [];
  private readonly locomotion: XRLocomotion;
  private hovered: Interactable | null = null;

  // Scratch for ray / desktop move — never allocate in update().
  private readonly _origin = new THREE.Vector3();
  private readonly _dir = new THREE.Vector3();
  private readonly _quat = new THREE.Quaternion();
  private readonly _ndc = new THREE.Vector2();
  private readonly _forward = new THREE.Vector3();
  private readonly _right = new THREE.Vector3();
  private readonly _move = new THREE.Vector3();
  private readonly _euler = new THREE.Euler();

  // Desktop free-look + movement state.
  private dragging = false;
  private movedWhileDragging = 0;
  private lastPointer = new THREE.Vector2();
  private yaw = 0;
  private pitch = 0;
  private readonly keys = new Set<string>();

  constructor(rig: XRRig) {
    this.rig = rig;
    this.yaw = rig.player.rotation.y;
    this.locomotion = new XRLocomotion(rig);
    this.setupControllers();
    this.setupDesktop();
  }

  /** Controllers for grab managers (left=0, right=1 when both present). */
  getControllers(): readonly ControllerHandle[] {
    return this.controllers;
  }

  addListener(listener: InputListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  register(interactable: Interactable): void {
    this.interactables.push(interactable);
    this.interactableObjects.push(interactable.object);
  }

  unregister(object: THREE.Object3D): void {
    const i = this.interactables.findIndex((it) => it.object === object);
    if (i >= 0) {
      this.interactables.splice(i, 1);
      this.interactableObjects.splice(i, 1);
    }
    if (this.hovered?.object === object) {
      this.hovered.onHover?.(false);
      this.hovered = null;
    }
  }

  private findInteractable(hit: THREE.Object3D | null): Interactable | null {
    if (!hit) return null;
    for (const it of this.interactables) {
      let node: THREE.Object3D | null = hit;
      while (node) {
        if (node === it.object) return it;
        node = node.parent;
      }
    }
    return null;
  }

  private setupControllers(): void {
    const { renderer, player } = this.rig;
    const rayGeom = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0, -1),
    ]);

    for (let i = 0; i < 2; i++) {
      const targetRay = renderer.xr.getController(i);
      const grip = renderer.xr.getControllerGrip(i);

      const ray = new THREE.Line(
        rayGeom,
        new THREE.LineBasicMaterial({ color: 0x66ccff }),
      );
      ray.name = "ray";
      ray.scale.z = 5;
      targetRay.add(ray);

      const tip = new THREE.Mesh(
        new THREE.SphereGeometry(0.012, 12, 12),
        new THREE.MeshBasicMaterial({ color: 0x66ccff }),
      );
      tip.name = "ray-tip";
      targetRay.add(tip);

      // Visible grip marker — hands specialist can replace with controller/hand models.
      const gripMarker = new THREE.Mesh(
        new THREE.BoxGeometry(0.03, 0.03, 0.08),
        new THREE.MeshStandardMaterial({
          color: 0x445566,
          roughness: 0.6,
          metalness: 0.2,
        }),
      );
      gripMarker.name = "grip-marker";
      grip.add(gripMarker);

      const handle: ControllerHandle = {
        index: i,
        targetRay,
        grip,
        selecting: false,
        squeezing: false,
        owned: null,
      };

      targetRay.addEventListener("selectstart", () => {
        handle.selecting = true;
        this.onControllerSelect(handle);
        for (const l of this.listeners) l.onSelectStart?.(handle);
      });
      targetRay.addEventListener("selectend", () => {
        handle.selecting = false;
        for (const l of this.listeners) l.onSelectEnd?.(handle);
      });
      targetRay.addEventListener("squeezestart", () => {
        handle.squeezing = true;
        for (const l of this.listeners) l.onSqueezeStart?.(handle);
      });
      targetRay.addEventListener("squeezeend", () => {
        handle.squeezing = false;
        for (const l of this.listeners) l.onSqueezeEnd?.(handle);
      });
      targetRay.addEventListener("connected", () => {
        for (const l of this.listeners) l.onControllerConnection?.(handle, true);
      });
      targetRay.addEventListener("disconnected", () => {
        // Notify grab managers first so they force-drop without snap/lock.
        // Do NOT synthesize squeeze-end here — GrabManager treats squeeze-end
        // as a placement attempt, which would incorrectly lock near a target
        // on Quest controller drop / battery / session teardown.
        for (const l of this.listeners) {
          l.onControllerConnection?.(handle, false);
        }
        handle.selecting = false;
        handle.squeezing = false;
        handle.owned = null;
      });

      player.add(targetRay);
      player.add(grip);
      this.controllers.push(handle);
    }
  }

  private clearControllerTransient(handle: ControllerHandle): void {
    const wasSelecting = handle.selecting;
    const wasSqueezing = handle.squeezing;
    handle.selecting = false;
    handle.squeezing = false;
    // Rising-edge grab managers should treat forced clear as release.
    if (wasSqueezing) {
      for (const l of this.listeners) l.onSqueezeEnd?.(handle);
    }
    if (wasSelecting) {
      for (const l of this.listeners) l.onSelectEnd?.(handle);
    }
    handle.owned = null;
  }

  private onControllerSelect(handle: ControllerHandle): void {
    this._origin.set(0, 0, 0);
    this._dir.set(0, 0, -1);
    handle.targetRay.getWorldPosition(this._origin);
    handle.targetRay.getWorldQuaternion(this._quat);
    this._dir.applyQuaternion(this._quat);
    this.raycaster.set(this._origin, this._dir);
    const hit = this.raycaster.intersectObjects(this.interactableObjects, true)[0];
    const it = this.findInteractable(hit ? hit.object : null);
    it?.onSelect();
  }

  private setupDesktop(): void {
    const el = this.rig.renderer.domElement;

    el.addEventListener("pointerdown", (e) => {
      this.dragging = true;
      this.movedWhileDragging = 0;
      this.lastPointer.set(e.clientX, e.clientY);
      el.setPointerCapture(e.pointerId);
    });

    el.addEventListener("pointermove", (e) => {
      if (!this.dragging) return;
      const dx = e.clientX - this.lastPointer.x;
      const dy = e.clientY - this.lastPointer.y;
      this.lastPointer.set(e.clientX, e.clientY);
      this.movedWhileDragging += Math.abs(dx) + Math.abs(dy);
      this.yaw -= dx * 0.0035;
      this.pitch -= dy * 0.0035;
      this.pitch = Math.max(-1.2, Math.min(1.2, this.pitch));
    });

    const endDrag = (e: PointerEvent) => {
      if (!this.dragging) return;
      this.dragging = false;
      el.releasePointerCapture?.(e.pointerId);
      // A small movement counts as a click -> selection.
      if (this.movedWhileDragging < 6) this.desktopSelect(e);
    };
    el.addEventListener("pointerup", endDrag);
    el.addEventListener("pointercancel", endDrag);

    window.addEventListener("keydown", (e) => this.keys.add(e.code));
    window.addEventListener("keyup", (e) => this.keys.delete(e.code));
  }

  private desktopSelect(e: PointerEvent): void {
    if (this.rig.renderer.xr.isPresenting) return;
    const el = this.rig.renderer.domElement;
    const rect = el.getBoundingClientRect();
    this._ndc.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(this._ndc, this.rig.camera);
    const hit = this.raycaster.intersectObjects(this.interactableObjects, true)[0];
    const it = this.findInteractable(hit ? hit.object : null);
    it?.onSelect();
  }

  private updateDesktopMovement(dt: number): void {
    if (this.rig.renderer.xr.isPresenting) return;
    const { player } = this.rig;
    player.rotation.set(0, this.yaw, 0);
    this.rig.camera.rotation.set(this.pitch, 0, 0);

    const speed = 2.4 * dt;
    this._euler.set(0, this.yaw, 0);
    this._forward.set(0, 0, -1).applyEuler(this._euler);
    this._right.set(1, 0, 0).applyEuler(this._euler);
    this._move.set(0, 0, 0);
    if (this.keys.has("KeyW") || this.keys.has("ArrowUp")) this._move.add(this._forward);
    if (this.keys.has("KeyS") || this.keys.has("ArrowDown")) this._move.sub(this._forward);
    if (this.keys.has("KeyD") || this.keys.has("ArrowRight")) this._move.add(this._right);
    if (this.keys.has("KeyA") || this.keys.has("ArrowLeft")) this._move.sub(this._right);
    if (this._move.lengthSq() > 0) {
      this._move.normalize().multiplyScalar(speed);
      player.position.add(this._move);
    }
  }

  private updateHover(): void {
    let current: Interactable | null = null;
    if (this.rig.renderer.xr.isPresenting && this.controllers.length > 0) {
      const controller = this.controllers[0]!;
      this._origin.set(0, 0, 0);
      this._dir.set(0, 0, -1);
      controller.targetRay.getWorldPosition(this._origin);
      controller.targetRay.getWorldQuaternion(this._quat);
      this._dir.applyQuaternion(this._quat);
      this.raycaster.set(this._origin, this._dir);
      const hit = this.raycaster.intersectObjects(this.interactableObjects, true)[0];
      current = this.findInteractable(hit ? hit.object : null);
    }
    if (current !== this.hovered) {
      this.hovered?.onHover?.(false);
      current?.onHover?.(true);
      this.hovered = current;
    }
  }

  /**
   * Reset transient input state (sticky select/squeeze, keys, hover, ownership,
   * locomotion axes / snap latch). Call on XR session start/end and trainer reset.
   */
  reset(): void {
    this.dragging = false;
    this.keys.clear();
    this.locomotion.reset();
    this.hovered?.onHover?.(false);
    this.hovered = null;
    for (const handle of this.controllers) {
      this.clearControllerTransient(handle);
    }
    // Align look state with current player yaw after pose resets.
    this.yaw = this.rig.player.rotation.y;
    this.pitch = 0;
  }

  update(dt: number): void {
    this.updateDesktopMovement(dt);
    this.locomotion.update(dt);
    // Keep desktop yaw in sync with snap-turns so exiting VR does not jump look.
    if (this.rig.renderer.xr.isPresenting) {
      this.yaw = this.rig.player.rotation.y;
    }
    this.updateHover();
  }
}
